import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faShareNodes } from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";
import Alert from "./Alert";
import MessagingService from "../../services/core/MessagingService";

// Same rationale as SaveFilterAsAlertModal: a superuser's user/group list mixes
// several tenants, so only label with the tenant when more than one is present.
const hasMultipleTenants = (items) => new Set((items || []).map((i) => i.tenantId).filter(Boolean)).size > 1;
const userLabel = (u, showTenant) =>
  `${u.displayName || u.email}${showTenant && u.tenantName ? ` · ${u.tenantName}` : ""}`;
const groupLabel = (g, showTenant) =>
  `${g.name} (${g.memberCount})${showTenant && g.tenantName ? ` · ${g.tenantName}` : ""}`;

const ShareFilterModal = ({ isOpen, onClose, filter, onShare }) => {
  const { t } = useTranslation();
  const [sharedUserIds, setSharedUserIds] = useState([]);
  const [sharedGroupIds, setSharedGroupIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !filter) return;
    setSharedUserIds(filter.sharedUserIds || []);
    setSharedGroupIds(filter.sharedGroupIds || []);
    setError("");
    Promise.all([MessagingService.users(), MessagingService.groups()])
      .then(([userRows, groupRows]) => {
        setUsers((userRows || []).filter((u) => u.messagingEnabled !== false));
        setGroups(groupRows || []);
      })
      .catch(() => {
        setUsers([]);
        setGroups([]);
      });
  }, [isOpen, filter]);

  const toggleUserId = (userId) =>
    setSharedUserIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );

  const toggleGroupId = (groupId) =>
    setSharedGroupIds((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );

  const submit = async (nextUserIds, nextGroupIds) => {
    setSaving(true);
    setError("");
    try {
      await onShare(filter.id, { sharedUserIds: nextUserIds, sharedGroupIds: nextGroupIds });
      onClose();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setSaving(false);
    }
  };

  const showUserTenant = useMemo(() => hasMultipleTenants(users), [users]);
  const showGroupTenant = useMemo(() => hasMultipleTenants(groups), [groups]);

  if (!filter) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("savedFilters.shareTitle")} icon={faShareNodes}>
      {error && <Alert type="error" message={error} onClose={() => setError("")} />}
      <p>{t("savedFilters.shareSubtitle", { description: filter.description })}</p>
      <div className="alert-recipients">
        <div className="alert-recipients-column">
          <strong>{t("alerts.fields.recipientUsers")}</strong>
          <div className="alert-recipients-list">
            {users.map((u) => (
              <label key={u.userId}>
                <input
                  type="checkbox"
                  checked={sharedUserIds.includes(u.userId)}
                  onChange={() => toggleUserId(u.userId)}
                />
                <span className="alert-recipient-label-text">{userLabel(u, showUserTenant)}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="alert-recipients-column">
          <strong>{t("alerts.fields.recipientGroups")}</strong>
          <div className="alert-recipients-list">
            {groups.map((g) => (
              <label key={g.id}>
                <input
                  type="checkbox"
                  checked={sharedGroupIds.includes(g.id)}
                  onChange={() => toggleGroupId(g.id)}
                />
                <span className="alert-recipient-label-text">{groupLabel(g, showGroupTenant)}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="modal-footer">
        {(filter.sharedUserIds?.length > 0 || filter.sharedGroupIds?.length > 0) && (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={saving}
            onClick={() => submit([], [])}
          >
            {t("savedFilters.stopSharing")}
          </button>
        )}
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={() => submit(sharedUserIds, sharedGroupIds)}
        >
          {t("common.save")}
        </button>
      </div>
    </Modal>
  );
};

export default ShareFilterModal;
